import {
  getGetClinicQueryKey,
  getListClinicActivityQueryKey,
  getListClinicsQueryKey,
  useUpdateClinic,
  type ClinicDetail,
} from '@pa/api/admin'
import { ConfirmDialog, toastApiError } from '@pa/staff'
import { Toggle, useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

/** S-04 Summary "Testing only" copy (the testing switch, `testingIgnorePfHours` on #41). */
export const TESTING_COPY = {
  title: 'Testing only',
  label: 'Testing only: ignore Practice Fusion facility hours',
  hintOff:
    'Off (normal): patients can only book inside Practice Fusion facility hours, and clinic hours must fit them.',
  hintOn:
    'On: patients can book any time the clinic is open in Clinic profile, even when Practice Fusion facilities are closed. Turn it off after the demo.',
  confirmTitle: 'Ignore Practice Fusion facility hours?',
  confirmBody:
    "For testing and demos only. Patients can book outside Practice Fusion facility hours (for example on weekends) once the clinic opens those hours in Clinic profile. This clinic's PF Keeper must have api.booking.allowAnyTimeForTesting: true in its config, or those bookings fail. Turn this off after the demo.",
  confirmAction: 'Turn on',
  doneOn: 'Testing switch turned on',
  doneOff: 'Testing switch turned off',
}

/**
 * S-04 Summary: the per-clinic testing switch (super admin only; any environment). Turning it on asks for
 * confirmation; turning it off doesn't. Saved at once with #41 `PATCH …/clinics/{id}` `{ testingIgnorePfHours }`
 * (audited as `clinic.update`).
 */
export function TestingSwitchCard({ clinic }: { clinic: ClinicDetail }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const update = useUpdateClinic()
  const [confirming, setConfirming] = useState(false)
  const on = clinic.testingIgnorePfHours

  async function save(next: boolean) {
    try {
      const updated = await update.mutateAsync({ clinicId: clinic.id, data: { testingIgnorePfHours: next } })
      queryClient.setQueryData(getGetClinicQueryKey(clinic.id), updated)
      void queryClient.invalidateQueries({ queryKey: getListClinicsQueryKey() })
      void queryClient.invalidateQueries({ queryKey: getListClinicActivityQueryKey(clinic.id) })
      toast(next ? TESTING_COPY.doneOn : TESTING_COPY.doneOff)
    } catch (err) {
      toastApiError(toast, err)
    } finally {
      setConfirming(false)
    }
  }

  return (
    <section className="card card-pad form-grid" aria-labelledby="s04-testing">
      <h2 className="form-section-title" id="s04-testing">
        {TESTING_COPY.title}
      </h2>
      <div className="row testing-switch">
        <Toggle
          checked={on}
          disabled={update.isPending}
          label={TESTING_COPY.label}
          onChange={(next) => {
            if (next) setConfirming(true)
            else void save(false)
          }}
        />
        <span aria-hidden>{TESTING_COPY.label}</span>
      </div>
      <p className="hint">{on ? TESTING_COPY.hintOn : TESTING_COPY.hintOff}</p>
      {confirming && (
        <ConfirmDialog
          title={TESTING_COPY.confirmTitle}
          body={TESTING_COPY.confirmBody}
          action={TESTING_COPY.confirmAction}
          pending={update.isPending}
          onConfirm={() => void save(true)}
          onCancel={() => {
            setConfirming(false)
          }}
        />
      )}
    </section>
  )
}
